import React, { useState, useEffect } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { toast } from 'sonner';
import { 
  Search, User, FileText, Car, Home, Briefcase, Heart, 
  AlertTriangle, CreditCard, Clock, CheckCircle, XCircle,
  ChevronRight, Package
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SearchResults = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState({ 
    clients: [], 
    assets: [], 
    quotes: [], 
    policies: [], 
    claims: [], 
    payments: [],
    pratiche: [],
    counts: { clients: 0, assets: 0, quotes: 0, policies: 0, claims: 0, payments: 0, pratiche: 0 }
  });
  // polizze archiviate e preventivi già convertiti: nascosti, salvo questa spunta
  const [archiviati, setArchiviati] = useState(false);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const q = searchParams.get('q');
    if (q) {
      setQuery(q);
      performSearch(q);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, archiviati]);

  const performSearch = async (searchQuery) => {
    if (!searchQuery.trim()) return;
    setLoading(true);
    try {
      const response = await axios.get(`${API}/global-search`, { params: { q: searchQuery, archiviati } });
      setResults(response.data);
    } catch (error) {
      toast.error('Errore nella ricerca');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    if (query.trim()) {
      setSearchParams({ q: query });
    }
  };

  const totalResults = results.counts.clients + results.counts.assets + results.counts.quotes + 
                       results.counts.policies + results.counts.claims + results.counts.payments + (results.counts.pratiche || 0);
  const nascosti = (results.nascosti_policies || 0) + (results.nascosti_quotes || 0);

  // Helper per formattare la data
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('it-IT');
  };

  // Helper per formattare importi
  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return '-';
    return new Intl.NumberFormat('it-IT', { style: 'currency', currency: 'EUR' }).format(amount);
  };

  // Icone per tipo asset
  const assetTypeIcons = {
    auto: Car,
    moto: Car,
    immobile: Home,
    attivita: Briefcase,
    salute: Heart
  };

  // Colori per stato polizza
  const policyStatusColors = {
    attiva: 'bg-green-100 text-green-700',
    sospesa: 'bg-yellow-100 text-yellow-700',
    scaduta: 'bg-red-100 text-red-700',
    archiviata: 'bg-slate-100 text-slate-700'
  };

  // Colori per stato preventivo
  const quoteStatusColors = {
    bozza: 'bg-slate-100 text-slate-700',
    inviato: 'bg-blue-100 text-blue-700',
    accettato: 'bg-green-100 text-green-700',
    rifiutato: 'bg-red-100 text-red-700',
    convertito: 'bg-purple-100 text-purple-700'
  };

  // Colori per stato sinistro
  const claimStatusColors = {
    aperto: 'bg-red-100 text-red-700',
    in_lavorazione: 'bg-yellow-100 text-yellow-700',
    chiuso: 'bg-green-100 text-green-700',
    rifiutato: 'bg-slate-100 text-slate-700'
  };

  // Colori per stato pagamento
  const paymentStatusColors = {
    pagato: 'bg-green-100 text-green-700',
    in_attesa: 'bg-yellow-100 text-yellow-700',
    scaduto: 'bg-red-100 text-red-700'
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="search-results-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          Centro di Controllo Rapido
        </h1>
        <p className="text-slate-600 mt-1">Cerca un cliente e visualizza tutti i dati collegati</p>
      </div>

      {/* Search Bar */}
      <Card className="border-slate-200 shadow-sm">
        <CardContent className="p-4">
          <form onSubmit={handleSearch}>
            <div className="relative">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Cerca per nome, cognome, codice fiscale, email o telefono..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 h-14 text-lg border-2 border-slate-200 focus:border-blue-500"
                data-testid="search-input"
              />
            </div>
          </form>
        </CardContent>
      </Card>

      {loading ? (
        <div className="text-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-slate-500">Ricerca in corso...</p>
        </div>
      ) : totalResults > 0 ? (
        <>
          {/* Quick Stats */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            <Card className={`border-2 ${results.counts.clients > 0 ? 'border-blue-200 bg-blue-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <User className={`w-6 h-6 mx-auto mb-1 ${results.counts.clients > 0 ? 'text-blue-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.clients}</p>
                <p className="text-xs text-slate-500">Clienti</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${results.counts.assets > 0 ? 'border-cyan-200 bg-cyan-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <Package className={`w-6 h-6 mx-auto mb-1 ${results.counts.assets > 0 ? 'text-cyan-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.assets}</p>
                <p className="text-xs text-slate-500">Beni</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${results.counts.quotes > 0 ? 'border-amber-200 bg-amber-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <FileText className={`w-6 h-6 mx-auto mb-1 ${results.counts.quotes > 0 ? 'text-amber-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.quotes}</p>
                <p className="text-xs text-slate-500">Preventivi</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${results.counts.policies > 0 ? 'border-green-200 bg-green-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <CheckCircle className={`w-6 h-6 mx-auto mb-1 ${results.counts.policies > 0 ? 'text-green-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.policies}</p>
                <p className="text-xs text-slate-500">Polizze</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${results.counts.claims > 0 ? 'border-red-200 bg-red-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <AlertTriangle className={`w-6 h-6 mx-auto mb-1 ${results.counts.claims > 0 ? 'text-red-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.claims}</p>
                <p className="text-xs text-slate-500">Sinistri</p>
              </CardContent>
            </Card>
            <Card className={`border-2 ${results.counts.payments > 0 ? 'border-purple-200 bg-purple-50' : 'border-slate-100'}`}>
              <CardContent className="p-3 text-center">
                <CreditCard className={`w-6 h-6 mx-auto mb-1 ${results.counts.payments > 0 ? 'text-purple-600' : 'text-slate-400'}`} />
                <p className="text-2xl font-bold text-slate-900">{results.counts.payments}</p>
                <p className="text-xs text-slate-500">Pagamenti</p>
              </CardContent>
            </Card>
          </div>

          <label className="flex items-center gap-2 text-sm text-slate-600">
            <input type="checkbox" checked={archiviati} onChange={(e) => setArchiviati(e.target.checked)} />
            Mostra anche archiviati (polizze archiviate, preventivi già convertiti, contratti cessati, appuntamenti passati)
            {!archiviati && nascosti > 0 && <span className="text-xs text-slate-400">— {nascosti} nascosti</span>}
          </label>

          {/* Risultati per categoria */}
          <div className="space-y-4">
            {/* PRATICHE DEI SERVIZI (Canone RAI, Ospitalità, Permessi, Dimissioni, CAF, utenze, telefonia…) */}
            {(results.pratiche || []).length > 0 && (
              <Card className="border-slate-200" data-testid="pratiche-results">
                <CardHeader className="pb-2 bg-orange-50 border-b">
                  <CardTitle className="text-base flex items-center gap-2">
                    <Briefcase className="w-5 h-5 text-orange-600" /> Pratiche e servizi ({results.pratiche.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.pratiche.map((p, i) => (
                    <Link key={`${p.modulo}-${p.id}-${i}`} to={p.link}
                      className={`flex items-center gap-3 px-4 py-2.5 border-t border-slate-900 first:border-t-0 hover:bg-slate-50 ${p.archiviata ? 'opacity-50' : ''}`}>
                      <Badge variant="outline" className="shrink-0 w-36 justify-center">{p.modulo}</Badge>
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-slate-900 truncate">{p.titolo}</div>
                        <div className="text-xs text-slate-500 truncate">{[p.client_name, p.dettaglio].filter(Boolean).join(' · ')}</div>
                      </div>
                      {p.stato && <span className="text-xs text-slate-600 capitalize shrink-0">{p.stato}</span>}
                      <span className="text-xs text-slate-400 w-20 text-right shrink-0">{p.data}</span>
                      <ChevronRight className="w-4 h-4 text-slate-300 shrink-0" />
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
            {/* CLIENTI */}
            {results.clients.length > 0 && (
              <Card className="border-slate-200" data-testid="clients-results">
                <CardHeader className="pb-2 bg-blue-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <User className="w-5 h-5 text-blue-600" />
                    Clienti ({results.clients.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.clients.map(client => (
                    <Link 
                      key={client.id} 
                      to={`/clients?open=${client.id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-full bg-blue-100 flex items-center justify-center">
                          <User className="w-5 h-5 text-blue-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {client.client_type === 'azienda' 
                              ? client.company_name 
                              : `${(client.last_name || '').toUpperCase()} ${(client.first_name || '').toUpperCase()}`.trim()}
                          </p>
                          <p className="text-sm text-slate-500">
                            {client.email} {client.phone && `• ${client.phone}`}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="font-mono bg-blue-100 text-blue-700">{client.client_code}</Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* BENI */}
            {results.assets.length > 0 && (
              <Card className="border-slate-200" data-testid="assets-results">
                <CardHeader className="pb-2 bg-cyan-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <Package className="w-5 h-5 text-cyan-600" />
                    Beni ({results.assets.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.assets.map(asset => {
                    const Icon = assetTypeIcons[asset.asset_type] || Package;
                    // Determina etichetta tipo asset per badge
                    const assetTypeLabels = {
                      'auto': 'Veicolo',
                      'moto': 'Moto',
                      'immobile': 'Fabbricato',
                      'fabbricato': 'Fabbricato',
                      'attivita': 'Attività',
                      'salute': 'Salute'
                    };
                    return (
                      <Link 
                        key={asset.id} 
                        to={`/clients?open=${asset.client_id}&asset=${asset.id}`}
                        className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                      >
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${
                            asset.asset_type === 'auto' || asset.asset_type === 'moto' ? 'bg-blue-100 text-blue-600' :
                            asset.asset_type === 'immobile' || asset.asset_type === 'fabbricato' ? 'bg-green-100 text-green-600' :
                            asset.asset_type === 'attivita' ? 'bg-purple-100 text-purple-600' :
                            asset.asset_type === 'salute' ? 'bg-red-100 text-red-600' :
                            'bg-cyan-100 text-cyan-600'
                          }`}>
                            <Icon className="w-5 h-5" />
                          </div>
                          <div>
                            <p className="font-semibold text-slate-900">{asset.asset_label || asset.asset_type}</p>
                            <p className="text-sm text-slate-500">{asset.client_name}</p>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="capitalize">
                            {assetTypeLabels[asset.asset_type] || asset.asset_type}
                          </Badge>
                          <ChevronRight className="w-4 h-4 text-slate-400" />
                        </div>
                      </Link>
                    );
                  })}
                </CardContent>
              </Card>
            )}

            {/* PREVENTIVI */}
            {results.quotes.length > 0 && (
              <Card className="border-slate-200" data-testid="quotes-results">
                <CardHeader className="pb-2 bg-amber-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <FileText className="w-5 h-5 text-amber-600" />
                    Preventivi ({results.quotes.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.quotes.map(quote => (
                    <Link 
                      key={quote.id} 
                      to={`/clients?open=${quote.client_id}&asset=${quote.asset_id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-amber-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {quote.company || 'Preventivo'} - {quote.quote_type}
                          </p>
                          <p className="text-sm text-slate-500">
                            {quote.client_name} • {formatCurrency(quote.premium)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={quoteStatusColors[quote.status] || 'bg-slate-100'}>
                          {quote.status}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* POLIZZE */}
            {results.policies.length > 0 && (
              <Card className="border-slate-200" data-testid="policies-results">
                <CardHeader className="pb-2 bg-green-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    Polizze ({results.policies.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.policies.map(policy => (
                    <Link 
                      key={policy.id} 
                      to={`/clients?open=${policy.client_id}&asset=${policy.asset_id}`}
                      className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-green-100 flex items-center justify-center">
                          <CheckCircle className="w-5 h-5 text-green-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {policy.policy_number || 'Polizza'} - {policy.company}
                          </p>
                          <p className="text-sm text-slate-500">
                            {policy.client_name} • Scade: {formatDate(policy.end_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={policyStatusColors[policy.status] || 'bg-slate-100'}>
                          {policy.status}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* SINISTRI */}
            {results.claims.length > 0 && (
              <Card className="border-slate-200" data-testid="claims-results">
                <CardHeader className="pb-2 bg-red-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <AlertTriangle className="w-5 h-5 text-red-600" />
                    Sinistri ({results.claims.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.claims.map(claim => (
                    <Link 
                      key={claim.id} 
                      to="/claims"
                      className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                          <AlertTriangle className="w-5 h-5 text-red-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            Sinistro #{claim.claim_number || claim.id.slice(0,8)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {claim.client_name} • Polizza: {claim.policy_number || '-'} • {formatDate(claim.claim_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={claimStatusColors[claim.status] || 'bg-slate-100'}>
                          {claim.status?.replace('_', ' ')}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* PAGAMENTI */}
            {results.payments.length > 0 && (
              <Card className="border-slate-200" data-testid="payments-results">
                <CardHeader className="pb-2 bg-purple-50 border-b">
                  <CardTitle className="text-lg flex items-center gap-2">
                    <CreditCard className="w-5 h-5 text-purple-600" />
                    Pagamenti ({results.payments.length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="p-0">
                  {results.payments.map(payment => (
                    <Link 
                      key={payment.id} 
                      to="/payments"
                      className="flex items-center justify-between p-4 hover:bg-slate-50 border-b last:border-b-0 transition-colors"
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-10 h-10 rounded-lg bg-purple-100 flex items-center justify-center">
                          <CreditCard className="w-5 h-5 text-purple-600" />
                        </div>
                        <div>
                          <p className="font-semibold text-slate-900">
                            {formatCurrency(payment.amount)}
                          </p>
                          <p className="text-sm text-slate-500">
                            {payment.client_name} • Scadenza: {formatDate(payment.due_date)}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className={paymentStatusColors[payment.status] || 'bg-slate-100'}>
                          {payment.status?.replace('_', ' ')}
                        </Badge>
                        <ChevronRight className="w-4 h-4 text-slate-400" />
                      </div>
                    </Link>
                  ))}
                </CardContent>
              </Card>
            )}
          </div>
        </>
      ) : searchParams.get('q') ? (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Nessun risultato trovato</h3>
            <p className="text-slate-500">Prova con un altro termine di ricerca</p>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-16 text-center">
            <Search className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-slate-700 mb-2">Inizia a cercare</h3>
            <p className="text-slate-500">Inserisci il nome di un cliente per visualizzare tutti i dati collegati</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default SearchResults;
